import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { 
  Search, Share2, Download, Copy, MapPin, Users, History, 
  Target, Twitter, Facebook, Linkedin, CheckCircle, RefreshCw,
  Vote, TrendingUp, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Party colors
const PARTY_COLORS = {
  conservative: { bg: '#0087DC', text: 'white', label: 'Conservative' },
  labour: { bg: '#E4003B', text: 'white', label: 'Labour' },
  libdem: { bg: '#FAA61A', text: 'white', label: 'Lib Dem' },
  reform: { bg: '#12B6CF', text: 'white', label: 'Reform UK' },
  green: { bg: '#6AB023', text: 'white', label: 'Green' },
  snp: { bg: '#FDF38E', text: 'black', label: 'SNP' },
  plaid: { bg: '#005B54', text: 'white', label: 'Plaid Cymru' },
  independent: { bg: '#808080', text: 'white', label: 'Independent' },
  other: { bg: '#666666', text: 'white', label: 'Other' }
};

const ShareConstituencyPage = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(false);
  const [constituencyData, setConstituencyData] = useState(null);
  const [mpData, setMpData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [predictionData, setPredictionData] = useState(null);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  // Search for constituency
  const searchConstituency = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setConstituencyData(null);
    setMpData(null);
    setHistoryData(null);
    
    try {
      // Search for constituency and MP
      const [constituencyRes, mpRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/elections/2024`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/mps`, { params: { search: searchQuery, limit: 5 } }).catch(() => ({ data: { mps: [] } })),
        axios.get(`${API}/api/election-results/constituency/${encodeURIComponent(searchQuery)}`).catch(() => ({ data: null }))
      ]);
      
      // Find matching constituency from 2024 results
      const constituencies = constituencyRes.data || [];
      const matchedConstituency = constituencies.find(c => 
        c.constituency?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      // Find matching MP
      const mps = mpRes.data?.mps || [];
      const matchedMP = mps.find(mp => 
        mp.constituency?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        mp.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      if (matchedConstituency || matchedMP) {
        setConstituencyData(matchedConstituency);
        setMpData(matchedMP);
        setHistoryData(historyRes.data);
        
        // Generate simple prediction
        if (matchedConstituency) {
          const currentParty = matchedConstituency.winner || matchedMP?.party?.toLowerCase();
          setPredictionData({
            likely_winner: currentParty,
            confidence: currentParty === 'labour' ? 'High' : currentParty === 'conservative' ? 'Medium' : 'Uncertain'
          });
        }
      } else {
        toast.error('No constituency found. Try a different search term.');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed. Please try again.');
    }
    
    setLoading(false);
  };

  // Download card as image
  const downloadCard = async () => {
    if (!cardRef.current) return;
    
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `${constituencyData?.constituency || mpData?.constituency || 'constituency'}-card.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Card downloaded!');
    } catch (error) {
      toast.error('Failed to download card');
    }
  };

  // Copy share link
  const copyShareLink = () => {
    const url = `${window.location.origin}/share?q=${encodeURIComponent(searchQuery)}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Share to social media
  const shareToSocial = (platform) => {
    const text = `Check out ${constituencyData?.constituency || mpData?.constituency}'s political profile on The Watchtower!`;
    const url = `${window.location.origin}/share?q=${encodeURIComponent(searchQuery)}`;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const constituencyName = constituencyData?.constituency || mpData?.constituency || '';
  const partyKey = (constituencyData?.winner || mpData?.party || 'other').toLowerCase();
  const partyConfig = PARTY_COLORS[partyKey] || PARTY_COLORS.other;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-20" data-testid="share-constituency-page">
      <SEO 
        title="Share Your Constituency"
        description="Generate shareable cards showing your local MP, voting history, and predictions. Perfect for social media!"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 py-16 text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur rounded-full mb-6">
            <Share2 className="h-4 w-4" />
            <span className="text-sm font-bold">Share & Engage</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-light mb-4">
            Share Your Constituency
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Generate beautiful shareable cards showing your local MP, voting history, and 2029 predictions
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="max-w-4xl mx-auto px-4 -mt-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Enter constituency name or postcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchConstituency()}
                className="w-full pl-12 pr-4 py-4 text-lg border border-slate-200 rounded-xl focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                data-testid="constituency-search-input"
              />
            </div>
            <Button
              onClick={searchConstituency}
              disabled={loading || !searchQuery.trim()}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-xl text-lg font-bold"
            >
              {loading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-3 text-center">
            Try: "Manchester Central", "Bristol West", "Edinburgh South"
          </p>
        </div>
      </section>

      {/* Results */}
      {(constituencyData || mpData) && (
        <section className="max-w-6xl mx-auto px-4 mt-12">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Shareable Card */}
            <div>
              <h2 className="font-bold text-xl mb-4 text-center">Your Shareable Card</h2>
              <div 
                ref={cardRef}
                className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
                data-testid="shareable-card"
              >
                {/* Card Header */}
                <div 
                  className="p-6 text-center"
                  style={{ backgroundColor: partyConfig.bg, color: partyConfig.text }}
                >
                  <div className="text-sm font-bold uppercase tracking-wider opacity-80 mb-2">
                    {partyConfig.label} Held
                  </div>
                  <h3 className="font-serif text-3xl font-light">
                    {constituencyName}
                  </h3>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  {/* Current MP */}
                  {mpData && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 text-sm text-slate-500 uppercase tracking-wider mb-2">
                        <Users className="h-4 w-4" />
                        Current MP
                      </div>
                      <div className="font-bold text-2xl">{mpData.name}</div>
                      <div className="text-slate-500">{partyConfig.label}</div>
                    </div>
                  )}

                  {/* 2024 Result */}
                  {constituencyData && (
                    <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-2 text-sm text-slate-500 uppercase tracking-wider mb-3">
                        <Vote className="h-4 w-4" />
                        2024 Election Result
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-sm text-slate-500">Winner</div>
                          <div className="font-bold" style={{ color: partyConfig.bg }}>
                            {partyConfig.label}
                          </div>
                        </div>
                        {constituencyData.majority && (
                          <div>
                            <div className="text-sm text-slate-500">Majority</div>
                            <div className="font-bold">{constituencyData.majority?.toLocaleString()}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* History Snapshot */}
                  {historyData?.history?.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 text-sm text-slate-500 uppercase tracking-wider mb-3">
                        <History className="h-4 w-4" />
                        Voting History (Last 5)
                      </div>
                      <div className="flex gap-1">
                        {historyData.history.slice(-5).map((election, idx) => (
                          <div 
                            key={idx}
                            className="flex-1 text-center p-2 rounded text-xs font-bold text-white"
                            style={{ backgroundColor: PARTY_COLORS[election.winner]?.bg || '#666' }}
                          >
                            {election.year?.toString().slice(-2)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 2029 Prediction */}
                  {predictionData && (
                    <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                      <div className="flex items-center gap-2 text-sm text-amber-700 uppercase tracking-wider mb-2">
                        <Target className="h-4 w-4" />
                        2029 Prediction
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold text-lg">
                            {PARTY_COLORS[predictionData.likely_winner]?.label || 'Unknown'} to Hold
                          </div>
                          <div className="text-sm text-slate-500">
                            Confidence: {predictionData.confidence}
                          </div>
                        </div>
                        <TrendingUp className="h-8 w-8 text-amber-500" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Footer */}
                <div className="px-6 py-4 bg-slate-900 text-white text-center">
                  <div className="font-serif text-lg">The Watchtower</div>
                  <div className="text-xs text-slate-400">www.the-watchtower.app</div>
                </div>
              </div>
            </div>

            {/* Share Options */}
            <div>
              <h2 className="font-bold text-xl mb-4 text-center">Share Options</h2>
              
              <div className="space-y-4">
                {/* Download */}
                <Button
                  onClick={downloadCard}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-4 rounded-xl font-bold text-lg"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download as Image
                </Button>

                {/* Copy Link */}
                <Button
                  onClick={copyShareLink}
                  variant="outline"
                  className="w-full border-2 border-slate-300 hover:border-slate-400 py-4 rounded-xl font-bold text-lg"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-5 w-5 mr-2" />
                      Copy Share Link
                    </>
                  )}
                </Button>

                {/* Social Share Buttons */}
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    onClick={() => shareToSocial('twitter')}
                    className="bg-black hover:bg-slate-800 text-white py-4 rounded-xl"
                  >
                    <Twitter className="h-5 w-5" />
                  </Button>
                  <Button
                    onClick={() => shareToSocial('facebook')}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl"
                  >
                    <Facebook className="h-5 w-5" />
                  </Button>
                  <Button
                    onClick={() => shareToSocial('linkedin')}
                    className="bg-blue-700 hover:bg-blue-800 text-white py-4 rounded-xl"
                  >
                    <Linkedin className="h-5 w-5" />
                  </Button>
                </div>

                {/* Tips */}
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <h3 className="font-bold text-purple-800 mb-2">💡 Sharing Tips</h3>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• Download the card for WhatsApp & Instagram</li>
                    <li>• Use the share link for Twitter threads</li>
                    <li>• Tag your local MP to start a conversation!</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!constituencyData && !mpData && !loading && (
        <section className="max-w-2xl mx-auto px-4 mt-16 text-center">
          <div className="bg-slate-50 rounded-2xl p-12 border border-slate-200">
            <Share2 className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-bold text-xl mb-2">Search for Your Constituency</h3>
            <p className="text-slate-500">
              Enter your constituency name above to generate a shareable card with your local MP, 
              voting history, and 2029 predictions.
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default ShareConstituencyPage;
